import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

/**
 * Returns the current user's referral code and a ready-to-share signup link.
 * Cached on the client so we don't hit the edge function on every page mount.
 */
const CACHE_KEY = "glowie_referral_v1";

interface CachedRef {
  uid: string;
  code: string;
  rewardReferred: number;
  rewardReferrer: number;
}

function readCache(uid: string): CachedRef | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedRef;
    return parsed.uid === uid ? parsed : null;
  } catch {
    return null;
  }
}

export function useReferralLink() {
  const { user } = useAuth();
  const cached = user ? readCache(user.id) : null;
  const [code, setCode] = useState<string | null>(cached?.code ?? null);
  const [rewardReferred, setRewardReferred] = useState<number>(cached?.rewardReferred ?? 5);
  const [rewardReferrer, setRewardReferrer] = useState<number>(cached?.rewardReferrer ?? 5);

  useEffect(() => {
    if (!user || code) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.functions.invoke("referrals", {
        body: { action: "get" },
      });
      if (cancelled || error || !data) return;
      const next: CachedRef = {
        uid: user.id,
        code: data.code,
        rewardReferred: data.rewards?.referred ?? 5,
        rewardReferrer: data.rewards?.referrer ?? 5,
      };
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(next));
      } catch {
        /* ignore storage errors */
      }
      setCode(next.code);
      setRewardReferred(next.rewardReferred);
      setRewardReferrer(next.rewardReferrer);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, code]);

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://meetglowie.com";
  const link = code ? `${origin}/auth?mode=signup&ref=${code}` : `${origin}/auth?mode=signup`;
  const text = code
    ? `Join me on Glowie. Use my code ${code} for ${rewardReferred} free credits.`
    : "Join me on Glowie, your AI skincare bestie.";

  return { code, link, text, rewardReferred, rewardReferrer };
}

export default useReferralLink;